import React, { useEffect, useState } from 'react';
import { Typography, Box, Fade, Stack } from '@mui/material';
import { useTypewriter } from '../../hooks';

type LegalTopic = 'Mietrecht' | 'Arbeitsrecht';

type LegalCategory = {
    headers: string[];
    infoText: string;
};

const mietrecht: LegalCategory = {
    headers: [
        "Mieterhöhung? Wir prüfen, ob sie rechtens ist.",
        "Kündigung wegen Eigenbedarf? Wir prüfen Ihre Rechte.",
        "Kündigung ohne Grund? Wir setzen uns für Sie ein.",
    ],
    infoText:
        "Akzeptieren Sie keine Kündigungen oder Mieterhöhungen ohne mit uns zu sprechen. Wir prüfen Ihre Situation sorgfältig und setzen Ihre Rechte durch - wenn nötig auch vor Gericht.",
};

const arbeitsrecht: LegalCategory = {
    headers: [
        "Fristlose Kündigung? Wir lassen das nicht stehen.",
        "Unfaire Behandlung am Arbeitsplatz? Wir sorgen für Gerechtigkeit.",
        "Abmahnung erhalten? Jetzt rechtlich absichern.",
    ],
    infoText:
        "Viele Arbeitnehmer akzeptieren eine Abmahnung oder sogar eine Kündigung, ohne ihre rechtlichen Ansprüche zu kennen. Wir prüfen Ihre Situation zuverlässig und kämpfen für Ihre Rechte – außergerichtlich oder vor dem Arbeitsgericht.",
};

const data: Record<LegalTopic, LegalCategory> = {
    Mietrecht: mietrecht,
    Arbeitsrecht: arbeitsrecht,
};

export const LegalHeadline: React.FC = () => {
    const [currentTopic, setCurrentTopic] = useState<LegalTopic>('Mietrecht');
    const [currentHeader, setCurrentHeader] = useState<string>(
        getRandomHeader('Mietrecht')
    );
    const [fadeIn, setFadeIn] = useState(true);
    const timer = 12000;
    const { displayedText } = useTypewriter(currentHeader);

    function getRandomHeader(topic: LegalTopic, exclude?: string): string {
        const headers = data[topic].headers;
        let newHeader = headers[Math.floor(Math.random() * headers.length)];
        while (newHeader === exclude && headers.length > 1) {
            newHeader = headers[Math.floor(Math.random() * headers.length)];
        }
        return newHeader;
    }

    useEffect(() => {
        const interval = setInterval(() => {
            setFadeIn(false);

            setTimeout(() => {
                setCurrentTopic((prev) => {
                    const nextTopic = prev === 'Mietrecht' ? 'Arbeitsrecht' : 'Mietrecht';
                    setCurrentHeader(getRandomHeader(nextTopic, currentHeader));
                    setFadeIn(true);
                    return nextTopic;
                });
            }, 300);
        }, timer);

        return () => clearInterval(interval);
    }, [currentHeader]);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
                minHeight: { xs: '70svh', md: '85vh' },
                width: '100%',
                overflow: 'hidden',
            }}
        >
            {/* Background image layer */}
            <Box
                component="img"
                src="/Banner.png"
                alt="Legal banner"
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    zIndex: 0,
                }}
            />

            {/* Overlay layer */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 1,
                }}
            />

            {/* Foreground content */}
            <Fade in={fadeIn} timeout={500}>
                <Stack
                    spacing={{ xs: 3, sm: 4, md: 6 }}
                    maxWidth="md"
                    margin="0 auto"
                    textAlign="center"
                    sx={{
                        position: 'relative',
                        zIndex: 2,
                        height: '100%',
                        justifyContent: 'center',
                        alignItems: 'center',
                        color: '#fff',
                        px: 2,
                    }}
                >
                    <Typography
                        variant="h3"
                        fontSize={{xs: "2.2rem", sm: '3.6rem'}}
                        fontWeight={700}
                        sx={{
                            minHeight: '120px',
                            '&::after': {
                                content: '"|"',
                                animation: 'blink 1s step-end infinite',
                                marginLeft: '4px',
                            },
                            '@keyframes blink': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0 },
                            },
                        }}
                    >
                        {displayedText}
                    </Typography>
                    <Typography variant="h5" color="inherit">
                        {data[currentTopic].infoText}
                    </Typography>
                </Stack>
            </Fade>
        </Box>
    );
};

